import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { API, endpoint } from './api-endpoints';
import { ThemeService } from './theme.service';
import { ToastService } from './toast.service';
import { hasAnyOrgSettingsPermission, hasPermission as permissionGranted } from './permissions';

export type AuthUser = {
  id: string;
  org_id?: string;
  email: string;
  first_name: string;
  last_name: string;
  permissions?: string[];
  role?: { id: string; name: string } | null;
};

export type NamedAssignment = { id: string; name: string; code?: string | null };

export type UserProfile = AuthUser & {
  about_you?: string | null;
  additional_responsibilities?: string | null;
  status?: string;
  created_at?: string;
  permissions?: string[];
  role?: { id: string; name: string; description?: string | null } | null;
  designation?: { id: string; name: string; roles_responsibilities?: string | null } | null;
  plants?: NamedAssignment[];
  departments?: NamedAssignment[];
  organization?: {
    id: string;
    name: string;
    address: string;
    is_super_org?: boolean;
    owner_id?: string | null;
  } | null;
};

type ApiResponse<T> = {
  success: boolean;
  message: string;
  data: T;
};

type AuthSession = {
  access_token: string;
  user: AuthUser;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly accessToken = signal<string | null>(null);
  readonly user = signal<AuthUser | null>(null);
  readonly isAuthenticated = computed(() => Boolean(this.accessToken() && this.user()));
  readonly hydrating = signal(true);
  readonly loading = signal(false);

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly toast: ToastService,
    private readonly theme: ThemeService,
  ) {}

  getAccessToken(): string | null {
    return this.accessToken();
  }

  async bootstrap(): Promise<void> {
    this.hydrating.set(true);
    try {
      const response = await firstValueFrom(this.http.post<ApiResponse<AuthSession>>(endpoint(API.auth.refresh), {}));
      this.setSession(response.data.access_token, response.data.user);
      await this.getProfile();
    } catch {
      this.clearSession();
    } finally {
      this.hydrating.set(false);
    }
  }

  async login(email: string, password: string) {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<AuthSession>>(endpoint(API.auth.login), {
        email,
        password,
      }),
    );
    this.setSession(response.data.access_token, response.data.user);
    await this.getProfile();
    return response;
  }

  async forgotPassword(email: string) {
    return firstValueFrom(
      this.http.post<ApiResponse<null>>(endpoint(API.auth.forgotPassword), { email }),
    );
  }

  async resetPassword(token: string, password: string, confirmPassword: string) {
    return firstValueFrom(
      this.http.post<ApiResponse<null>>(endpoint(API.auth.resetPassword), {
        token,
        password,
        confirm_password: confirmPassword,
      }),
    );
  }

  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string) {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<null>>(endpoint(API.auth.changePassword), {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    );
    this.clearSession();
    return response;
  }

  async getProfile(): Promise<UserProfile> {
    const response = await firstValueFrom(this.http.get<ApiResponse<UserProfile>>(endpoint(API.profile.root)));
    this.mergeUser(response.data);
    return response.data;
  }

  hasPermission(key: string): boolean {
    return permissionGranted(this.user()?.permissions, key);
  }

  canAccessOrgSettings(): boolean {
    return hasAnyOrgSettingsPermission(this.user()?.permissions);
  }

  async updateProfile(payload: {
    first_name?: string;
    last_name?: string;
    about_you?: string;
  }): Promise<ApiResponse<UserProfile>> {
    const response = await firstValueFrom(
      this.http.patch<ApiResponse<UserProfile>>(endpoint(API.auth.me), payload),
    );
    this.mergeUser(response.data);
    return response;
  }

  async logout() {
    try {
      const response = await firstValueFrom(this.http.post<ApiResponse<null>>(endpoint(API.auth.logout), {}));
      this.clearSession();
      this.toast.success(response.message);
    } finally {
      this.clearSession();
      await this.router.navigateByUrl('/login');
    }
  }

  private refreshing: Promise<string | null> | null = null;

  /**
   * One refresh at a time: concurrent 401s share it, because each refresh rotates the
   * refresh cookie and parallel ones would invalidate each other. A failed refresh ends
   * the session and sends the user to login.
   */
  refreshAccessToken(): Promise<string | null> {
    this.refreshing ??= firstValueFrom(this.http.post<ApiResponse<AuthSession>>(endpoint(API.auth.refresh), {}))
      .then((response) => {
        this.setSession(response.data.access_token, response.data.user);
        return response.data.access_token;
      })
      .catch(async (err) => {
        this.clearSession();
        await this.router.navigateByUrl('/login');
        throw err;
      })
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  private setSession(token: string, user: AuthUser): void {
    this.accessToken.set(token);
    this.user.set(user);
    void this.theme.load();
  }

  private mergeUser(profile: UserProfile): void {
    this.user.set({
      id: profile.id,
      org_id: profile.org_id,
      email: profile.email,
      first_name: profile.first_name,
      last_name: profile.last_name,
      permissions: profile.permissions ?? this.user()?.permissions ?? [],
      role: profile.role ? { id: profile.role.id, name: profile.role.name } : this.user()?.role ?? null,
    });
  }

  private clearSession(): void {
    this.accessToken.set(null);
    this.user.set(null);
    this.theme.clear();
  }
}
